import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Plus, MessageSquare, Mail, Share2, Search, FileSpreadsheet, X } from "lucide-react";
import LogbookTaskCard, { Field, lbSelect } from "./LogbookTaskCard";
import LogbookImport from "./LogbookImport";
import {
  isTaskDone, isOverdue, isStale, efficiencyPct, avg, effColor, sortNewestFirst, visibleTeamUserIds,
  canSeeTeamBoard, assignableUsersFor, orgContextLabel, teamSearchMatches, addRepeat, isoDate,
  teamSummaryText, whatsappUrl,
} from "./logbookUtils";

export function Stats({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(([label, value, color]) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <div className="text-2xl font-bold" style={{ color: color || "#0f172a", fontFamily: "Manrope" }}>{value}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        </div>
      ))}
    </div>
  );
}

export function SectionHead({ title, count, children }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
      <h2 className="text-base font-semibold text-slate-900">{title}{count !== undefined && <span className="ml-2 text-xs font-normal text-slate-500">{count}</span>}</h2>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
      <div className="text-[11px] font-mono tracking-[0.2em] text-slate-400 mb-1">— {title} —</div>{children}
    </div>
  );
}

const avgEff = (statuses, tasks) => avg(tasks.filter((t) => isTaskDone(statuses, t)).map((t) => efficiencyPct(statuses, t)).filter((v) => v !== null));
const pctText = (v) => (v === null ? "—" : `${v}%`);

/* ---------------- My Tasks ---------------- */
export function MyTasks({ ctx }) {
  const { me, data } = ctx;
  const { statuses } = data;
  const mine = sortNewestFirst(data.tasks.filter((t) => t.assignedTo === me.id));
  const done = mine.filter((t) => isTaskDone(statuses, t));
  const eff = avgEff(statuses, mine);
  return (
    <div className="space-y-4" data-testid="lb-mine">
      <Stats items={[["Open tasks", mine.length - done.length], ["Completed", done.length], ["Avg efficiency", pctText(eff), effColor(eff)]]} />
      <SectionHead title="My board" count={`${mine.length} total`} />
      {mine.length === 0 ? <Empty title="NO ENTRIES">Nothing assigned to you yet. Use Assign Task to log a new entry.</Empty>
        : mine.map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} showActions />)}
    </div>
  );
}

/* ---------------- Assigned by Me ---------------- */
export function Given({ ctx }) {
  const { me, data } = ctx;
  const { statuses } = data;
  const given = sortNewestFirst(data.tasks.filter((t) => t.assignedBy === me.id && t.assignedTo !== me.id));
  const overdue = given.filter((t) => isOverdue(statuses, t)).length;
  return (
    <div className="space-y-4" data-testid="lb-given">
      <Stats items={[["Handed out", given.length], ["Still open", given.filter((t) => !isTaskDone(statuses, t)).length], ["Overdue", overdue, overdue ? "#dc2626" : undefined]]} />
      <SectionHead title="Handed out" count={`${given.length} total`} />
      {given.length === 0 ? <Empty title="NOTHING HANDED OUT">Tasks you assign to someone else show up here so you can track them.</Empty>
        : given.map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} />)}
    </div>
  );
}

/* ---------------- Team Board ---------------- */
export function Team({ ctx }) {
  const { me, data, act } = ctx;
  const { statuses } = data;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [rosterFilter, setRosterFilter] = useState("all");
  const [viewFor, setViewFor] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [empFilter, setEmpFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);

  const scopeIds = useMemo(() => new Set(visibleTeamUserIds(me, data.users)), [me, data.users]);
  const users = data.users.filter((u) => scopeIds.has(u.id));
  const tasks = data.tasks.filter((t) => scopeIds.has(t.assignedTo));
  if (!canSeeTeamBoard(me)) {
    return <Empty title="HEADS AND ADMINS">The Team board is for department heads, sub-department heads and Logbook admins.</Empty>;
  }

  const assign = (uid) => navigate("/hr/logbook/assign", { state: { assignee: uid } });
  const overdueAll = tasks.filter((t) => isOverdue(statuses, t)).length;
  const teamEff = avgEff(statuses, tasks);
  const hits = teamSearchMatches(search, users);
  const sel = users.find((u) => u.id === selected);
  const selTasks = sel ? sortNewestFirst(tasks.filter((t) => t.assignedTo === sel.id))
    .sort((a, b) => Number(isTaskDone(statuses, a)) - Number(isTaskDone(statuses, b))) : [];
  const displayed = rosterFilter === "all" ? users : users.filter((u) => u.id === rosterFilter);
  const attention = tasks.filter((t) => !isTaskDone(statuses, t) && (isOverdue(statuses, t) || isStale(statuses, t)));
  let all = sortNewestFirst(tasks);
  if (empFilter !== "all") all = all.filter((t) => t.assignedTo === empFilter);
  if (statusFilter !== "all") all = all.filter((t) => t.status === statusFilter);
  const byName = users.slice().sort((a, b) => a.name.localeCompare(b.name));

  const sendSummary = async () => {
    if (!window.confirm("Email the team summary to the recipients set on the Logbook server?")) return;
    await act("send_summary", {}, "Summary email sent");
  };

  return (
    <div className="space-y-4" data-testid="lb-team">
      <Stats items={[["Total tasks", tasks.length], ["Open", tasks.filter((t) => !isTaskDone(statuses, t)).length], ["Overdue", overdueAll, "#dc2626"], ["Team efficiency", pctText(teamEff), effColor(teamEff)]]} />
      <SectionHead title="Roster" count={`${users.length} people`}>
        {me.isAdmin && <>
          <Button size="sm" variant="outline" onClick={() => setImportOpen((v) => !v)} data-testid="lb-import-toggle">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />{importOpen ? "Close import" : "Import Excel"}
          </Button>
          <Button size="sm" variant="outline" onClick={sendSummary}><Mail className="w-3.5 h-3.5 mr-1" />Email summary</Button>
          <Button size="sm" variant="outline" asChild>
            <a href={whatsappUrl(`*LOGBOOK Team Summary* — ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}\n\n${teamSummaryText({ users, tasks, statuses })}`)} target="_blank" rel="noopener noreferrer">
              <Share2 className="w-3.5 h-3.5 mr-1" />Share via WhatsApp
            </a>
          </Button>
        </>}
      </SectionHead>
      {importOpen && me.isAdmin && <LogbookImport ctx={ctx} onClose={() => setImportOpen(false)} />}

      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
        <label htmlFor="lb-team-search" className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Find an employee</label>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input id="lb-team-search" className="pl-8" placeholder="Type part of a name" value={search} autoComplete="off"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && hits[0]) { setSelected(hits[0].id); setSearch(""); }
              if (e.key === "Escape") setSearch("");
            }} data-testid="lb-team-search" />
        </div>
        {search.trim() && (hits.length === 0 ? <div className="text-sm text-slate-500 px-1">No employee matches “{search.trim()}”.</div> : (
          <div className="divide-y divide-slate-100">
            {hits.slice(0, 8).map((u) => (
              <button key={u.id} type="button" className="w-full flex items-center justify-between gap-3 px-2 py-2 text-left hover:bg-slate-50 rounded"
                onClick={() => { setSelected(u.id); setSearch(""); if (viewFor === u.id) setViewFor(null); }}>
                <span><span className="block text-sm font-medium text-slate-800">{u.name}</span>
                  <span className="block text-xs text-slate-500">{orgContextLabel(u, data.departments, data.subDepartments)}</span></span>
                <span className="text-xs rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">{tasks.filter((t) => t.assignedTo === u.id && !isTaskDone(statuses, t)).length} open</span>
              </button>
            ))}
            {hits.length > 8 && <p className="text-xs text-slate-500 pt-2">{hits.length - 8} more — keep typing to narrow it down.</p>}
          </div>
        ))}
      </div>

      {sel && (
        <div className="space-y-3" data-testid="lb-team-selected">
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div><div className="font-semibold text-slate-900">{sel.name}</div>
                <div className="text-xs text-slate-500">{orgContextLabel(sel, data.departments, data.subDepartments)}</div></div>
              <Button size="sm" variant="outline" onClick={() => setSelected(null)}><X className="w-3.5 h-3.5 mr-1" />Close</Button>
            </div>
            <Stats items={[["Open", selTasks.filter((t) => !isTaskDone(statuses, t)).length], ["Overdue", selTasks.filter((t) => isOverdue(statuses, t)).length], ["Completed", selTasks.filter((t) => isTaskDone(statuses, t)).length]]} />
            <Button size="sm" onClick={() => assign(sel.id)}><Plus className="w-3.5 h-3.5 mr-1" />Assign task</Button>
          </div>
          <SectionHead title={`Tasks assigned to ${sel.name}`} count={`${selTasks.length} total`} />
          {selTasks.length === 0 ? <Empty title="NO TASKS">Nothing assigned to {sel.name} yet.</Empty>
            : selTasks.map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} comments="full" />)}
        </div>
      )}

      <Field label="Filter employee" className="max-w-xs">
        <select className={lbSelect} value={rosterFilter} onChange={(e) => setRosterFilter(e.target.value)}>
          <option value="all">All employees</option>
          {byName.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </Field>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="lb-roster">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-3 py-2">Name</th><th className="px-3 py-2">Open</th><th className="px-3 py-2">Overdue</th><th className="px-3 py-2">Completed</th><th className="px-3 py-2 text-left">Efficiency</th><th className="px-3 py-2 text-left">Actions</th></tr>
          </thead>
          <tbody>
            {displayed.map((u) => {
              const ut = tasks.filter((t) => t.assignedTo === u.id);
              const od = ut.filter((t) => isOverdue(statuses, t)).length;
              const e = avgEff(statuses, ut);
              return (
                <Fragment key={u.id}>
                  <tr className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{u.name}</td>
                    <td className="px-3 py-2 text-center font-mono">{ut.filter((t) => !isTaskDone(statuses, t)).length}</td>
                    <td className="px-3 py-2 text-center font-mono" style={{ color: od ? "#dc2626" : "#94a3b8" }}>{od}</td>
                    <td className="px-3 py-2 text-center font-mono">{ut.filter((t) => isTaskDone(statuses, t)).length}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden"><div className="h-full" style={{ width: `${e === null ? 0 : Math.min(e, 100)}%`, background: effColor(e) }} /></div>
                        <span className="font-mono text-xs" style={{ color: effColor(e) }}>{pctText(e)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setViewFor(viewFor === u.id ? null : u.id); if (selected === u.id) setSelected(null); }}><Eye className="w-3.5 h-3.5 mr-1" />{viewFor === u.id ? "Hide" : "View"}</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => assign(u.id)}><Plus className="w-3.5 h-3.5 mr-1" />Assign</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setCommentFor(commentFor === u.id ? null : u.id); setCommentText(""); }}><MessageSquare className="w-3.5 h-3.5 mr-1" />Comment</Button>
                      </div>
                    </td>
                  </tr>
                  {viewFor === u.id && (
                    <tr><td colSpan={6} className="bg-slate-50 p-3 space-y-3">
                      {ut.length === 0 ? <Empty title="NO TASKS">Nothing assigned to {u.name} yet.</Empty>
                        : sortNewestFirst(ut).map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} comments="full" />)}
                    </td></tr>
                  )}
                  {commentFor === u.id && (
                    <tr><td colSpan={6} className="bg-slate-50 p-3 space-y-2">
                      <Field label={`Note for ${u.name}`}>
                        <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Write a note — it'll show up in their notifications…" />
                      </Field>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={async () => {
                          const message = commentText.trim();
                          if (!message) return ctx.toastError("Write something first");
                          if (await act("send_comment", { targetUserId: u.id, message }, "Comment sent")) setCommentFor(null);
                        }}>Send</Button>
                        <Button size="sm" variant="outline" onClick={() => setCommentFor(null)}>Cancel</Button>
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {displayed.length === 0 && <Empty title="NO ROSTER">No one in your team yet.</Empty>}
      </div>

      {attention.length > 0 && <>
        <SectionHead title="Needs attention" count={attention.length} />
        {attention.map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} comments="none" />)}
      </>}

      <SectionHead title="All tasks" count={`${all.length} of ${tasks.length}`} />
      <div className="grid grid-cols-2 gap-3 max-w-lg">
        <Field label="Employee">
          <select className={lbSelect} value={empFilter} onChange={(e) => setEmpFilter(e.target.value)}>
            <option value="all">All employees</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className={lbSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      </div>
      {all.length === 0 ? <Empty title="NO MATCHES">Try a different filter.</Empty>
        : all.map((t) => <LogbookTaskCard key={t.id} ctx={ctx} t={t} comments="none" />)}
    </div>
  );
}

/* ---------------- Assign Task ---------------- */
export function Assign({ ctx, preselect }) {
  const { me, data, act } = ctx;
  const navigate = useNavigate();
  const allowed = assignableUsersFor(me, data.users);
  const others = allowed.filter((u) => u.id !== me.id);
  const pre = preselect && others.some((u) => u.id === preselect) ? preselect : null;
  const defaultStatus = data.statuses.find((s) => !s.isDone) || data.statuses[0];
  const [self, setSelf] = useState(!pre);
  const [f, setF] = useState({
    title: "", description: "", assignee: pre || others[0]?.id || me.id, statusId: defaultStatus?.id || "",
    dueDate: "", est: "", repeatOn: false, repeatType: "weekly", startDate: isoDate(new Date()),
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isHead = me.isAdmin || me.orgRole === "dept_head" || me.orgRole === "sub_head";

  const submit = async () => {
    if (!f.title.trim()) return ctx.toastError("Give the task a title");
    if (f.repeatOn && !f.startDate) return ctx.toastError("Pick a start date for the repeated work");
    setBusy(true);
    const res = await act("add_task", {
      title: f.title.trim(), description: f.description.trim() || null, assignedTo: self ? me.id : f.assignee,
      dueDate: f.repeatOn ? null : f.dueDate || null, estimatedHours: Number(f.est) || null, statusId: f.statusId,
      repeatType: f.repeatOn ? f.repeatType : "none", startDate: f.repeatOn ? f.startDate : null,
    });
    setBusy(false);
    if (res) {
      ctx.toastOk(f.repeatOn ? `Repeating task added — due ${res.dueDate || ""}` : "Task added");
      navigate(self ? "/hr/logbook/mine" : "/hr/logbook/given");
    }
  };

  return (
    <div className="max-w-2xl rounded-lg border border-slate-200 bg-white p-4 space-y-4" data-testid="lb-assign">
      <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
        <button type="button" className={`px-3 py-1.5 text-sm rounded ${self ? "bg-white shadow-sm font-medium" : "text-slate-600"}`} onClick={() => setSelf(true)}>For myself</button>
        <button type="button" disabled={!others.length} title={others.length ? "" : "No one you can assign to yet"}
          className={`px-3 py-1.5 text-sm rounded disabled:opacity-40 ${!self ? "bg-white shadow-sm font-medium" : "text-slate-600"}`} onClick={() => setSelf(false)} data-testid="lb-assign-other">For someone else</button>
      </div>
      {!others.length && <p className="text-xs text-slate-500">{isHead ? "Add employees under Logbook Admin → Employees to assign tasks to them." : "Only department and sub-department heads can assign tasks to others — you can still assign tasks to yourself."}</p>}
      <Field label="Task title"><Input value={f.title} onChange={set("title")} placeholder="e.g. Inspect flange batch #221" data-testid="lb-assign-title" /></Field>
      <Field label="Description (optional)"><Textarea value={f.description} onChange={set("description")} placeholder="Any context worth logging" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {self ? <div className="text-sm text-slate-600 self-end pb-2">Assigned to <b>you</b> ({me.name}).</div> : (
          <Field label="Assign to">
            <select className={lbSelect} value={f.assignee} onChange={set("assignee")} data-testid="lb-assign-to">
              {others.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Starting status">
          <select className={lbSelect} value={f.statusId} onChange={set("statusId")}>
            {data.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {!f.repeatOn && <Field label="Due date"><Input type="date" value={f.dueDate} onChange={set("dueDate")} /></Field>}
        <Field label="Estimated hours"><Input type="number" min="0" step="0.5" value={f.est} onChange={set("est")} placeholder="e.g. 6" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Checkbox checked={f.repeatOn} onCheckedChange={(v) => setF({ ...f, repeatOn: !!v })} /> Repeated work (Daily / Weekly / Monthly)
      </label>
      {f.repeatOn && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Work type">
              <select className={lbSelect} value={f.repeatType} onChange={set("repeatType")}>
                <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
            </Field>
            <Field label="Start date"><Input type="date" value={f.startDate} onChange={set("startDate")} /></Field>
          </div>
          <p className="text-xs text-slate-500">Due date (auto-calculated): <b>{addRepeat(f.startDate, f.repeatType) || "—"}</b> — a new task is generated automatically each time this one is completed or renewed.</p>
        </div>
      )}
      <Button onClick={submit} disabled={busy} data-testid="lb-assign-submit"><Plus className="w-4 h-4 mr-1" />Add to board</Button>
    </div>
  );
}
